import {
  HStack,
  type IconProps,
  Label,
  Text,
  Tooltip,
} from '@adc/parallax-component-library';
import type { LabelType } from '@adc/parallax-component-library/types/components/Atoms/Label/common';

import {
  type PatientData,
  SENSOR_STATUS,
  type SensorStatus,
} from '~/api/types';
import { formatTimeDistance } from '~/utils/dateUtils';

import {
  AlertsMutedIcon,
  CautionIcon,
  SuccessIcon,
  WarningIcon,
} from './Icons';
import type { PatientTableColumnKey } from './types';

const SENSOR_STATUS_NAME: Record<SensorStatus, string> = {
  [SENSOR_STATUS.ConnectionRequest]: 'Connection Request',
  [SENSOR_STATUS.ActiveEndingSoon]: 'Active - Ending soon',
  [SENSOR_STATUS.ActiveNoData]: 'Active - No data',
  [SENSOR_STATUS.InactiveActivationPending]: 'Inactive - Activation pending',
  [SENSOR_STATUS.Active]: 'Active',
  [SENSOR_STATUS.ActiveWarmingUp]: 'Active - Warming up',
  [SENSOR_STATUS.InactiveSensorEnded]: 'Inactive - Sensor ended',
  [SENSOR_STATUS.InactiveLastData]: 'Inactive - Last data',
};

type SensorStatusLabelOptions = {
  timestamp?: string;
  displayValue?: string | number;
};

export const SENSOR_STATUS_LABEL: Record<
  SensorStatus,
  (opts?: string | SensorStatusLabelOptions) => string
> = {
  [SENSOR_STATUS.ConnectionRequest]: () => 'Connection Request',

  [SENSOR_STATUS.ActiveEndingSoon]: (opts) => {
    const { timestamp, displayValue } =
      typeof opts === 'string' ? { timestamp: opts } : (opts ?? {});
    const value =
      displayValue ??
      (timestamp
        ? formatTimeDistance(timestamp, { short: true, suffix: 'left' })
        : '');

    return `Active - Ending soon: ${value}`;
  },

  [SENSOR_STATUS.ActiveNoData]: (opts) => {
    const { timestamp, displayValue } =
      typeof opts === 'string' ? { timestamp: opts } : (opts ?? {});
    const value =
      displayValue ??
      (timestamp ? formatTimeDistance(timestamp, { short: true }) : '');

    return `Active - No data for ${value}`;
  },

  [SENSOR_STATUS.InactiveActivationPending]: () =>
    'Inactive - Activation pending',

  [SENSOR_STATUS.Active]: () => 'Active',

  [SENSOR_STATUS.ActiveWarmingUp]: (opts) => {
    const { timestamp, displayValue } =
      typeof opts === 'string' ? { timestamp: opts } : (opts ?? {});
    const value =
      displayValue ??
      (timestamp
        ? formatTimeDistance(timestamp, { short: true, suffix: 'left' })
        : '');

    return `Active - Warming up: ${value}`;
  },

  [SENSOR_STATUS.InactiveSensorEnded]: (opts) => {
    const { timestamp, displayValue } =
      typeof opts === 'string' ? { timestamp: opts } : (opts ?? {});
    const value =
      displayValue ??
      (timestamp
        ? formatTimeDistance(timestamp, { short: true, suffix: 'ago' })
        : '');

    return `Inactive - Sensor ended ${value}`;
  },

  [SENSOR_STATUS.InactiveLastData]: (opts) => {
    const { timestamp, displayValue } =
      typeof opts === 'string' ? { timestamp: opts } : (opts ?? {});
    const value =
      displayValue ??
      (timestamp
        ? formatTimeDistance(timestamp, { short: true, suffix: 'ago' })
        : '');

    return `Inactive - Last data ${value}`;
  },
};

export const SENSOR_STATUS_VARIANT: Record<SensorStatus, LabelType> = {
  [SENSOR_STATUS.ConnectionRequest]: 'caution',
  [SENSOR_STATUS.ActiveEndingSoon]: 'caution',
  [SENSOR_STATUS.ActiveNoData]: 'caution',
  [SENSOR_STATUS.InactiveActivationPending]: 'caution',
  [SENSOR_STATUS.Active]: 'success',
  [SENSOR_STATUS.ActiveWarmingUp]: 'success',
  [SENSOR_STATUS.InactiveSensorEnded]: 'generic',
  [SENSOR_STATUS.InactiveLastData]: 'generic',
} as const;

export const SENSOR_STATUS_ICON: Record<
  SensorStatus,
  React.ReactElement<IconProps> | undefined
> = {
  [SENSOR_STATUS.ConnectionRequest]: <CautionIcon />,
  [SENSOR_STATUS.ActiveEndingSoon]: <CautionIcon />,
  [SENSOR_STATUS.ActiveNoData]: <CautionIcon />,
  [SENSOR_STATUS.InactiveActivationPending]: <CautionIcon />,
  [SENSOR_STATUS.Active]: <SuccessIcon />,
  [SENSOR_STATUS.ActiveWarmingUp]: <SuccessIcon />,
  [SENSOR_STATUS.InactiveSensorEnded]: undefined,
  [SENSOR_STATUS.InactiveLastData]: undefined,
} as const;

type FormattersMap = Record<
  PatientTableColumnKey,
  (value: PatientData) => React.ReactNode
>;

const isAlert = (value: number) => value > 2.0;

export const patientTableColumnFormatters: FormattersMap = {
  lastName: (value) => value.lastName,
  firstName: (value) => value.firstName,
  phoneNumberLast4Digits: (value) => value.phoneNumberLast4Digits,
  dateOfBirth: (value) =>
    value.dateOfBirth ? new Date(value.dateOfBirth).toLocaleDateString() : '',
  alertsLast24hours: (value) =>
    value.alertsLast24hours == undefined ? (
      '---'
    ) : (
      <HStack gap="$5">
        <Text>{value.alertsLast24hours}</Text>
        <Tooltip text="Patient turned off alerts">
          {value.alertState === 'muted' && <AlertsMutedIcon />}
        </Tooltip>
        <Tooltip text="High Lactate Alert">
          {value.alertsLast24hours > 0 && <WarningIcon />}
        </Tooltip>
      </HStack>
    ),
  lactate: (value) => {
    if (isAlert(value.lactate)) {
      return <b>{`${value.lactate.toFixed(1)} mmol/L`}</b>;
    }

    return `${value.lactate.toFixed(1)} mmol/L`;
  },
  status: (value) => (
    <Label
      variant="tertiary"
      iconStart={SENSOR_STATUS_ICON[value.status.status]}
      type={SENSOR_STATUS_VARIANT[value.status.status]}
      aria-label={SENSOR_STATUS_NAME[value.status.status]}
    >
      <Text>
        {SENSOR_STATUS_LABEL[value.status.status](value.status.timestamp)}
      </Text>
    </Label>
  ),
};
